import { useSelector } from "react-redux";
import {
  useWorkflowVisibility,
  isWorkflowVisible,
} from "@dashai/hooks/useWorkflowVisibility";
import ModuleAccessDenied from "./ModuleAccessDenied";
import PageLoadingFallback from "./PageLoadingFallback";

/**
 * Route guard enforcing per-workspace workflow visibility for a gated dashboard
 * page (Key Accounts, Accounts). The sibling of ModuleProtectedRoute: that one
 * gates on static product scope, this one gates on the async workflow-visibility
 * registry so the route can't be reached by direct URL in a workspace where the
 * page's sidebar entry is hidden.
 *
 * Renders its children only when the active workspace's visibility set enables
 * `workflowId`; otherwise renders the in-shell Access Denied page (redirect-loop
 * safe, matching ModuleProtectedRoute). Wrap a page INSIDE GeneralLayout so the
 * denied state keeps the app shell.
 *
 * @param {{ workflowId: string, children: React.ReactNode }} props
 */
const WorkflowVisibilityRoute = ({ workflowId, children }) => {
  const selectedClient = useSelector((s) => s.auth.selectedWorkspace?.key ?? null);
  const { visibleWorkflowIds, status } = useWorkflowVisibility(selectedClient);

  // No workspace selected → deny (with the escape CTA), don't spin forever.
  // ProtectedRoute only renders routes after userData has loaded, and the
  // fetch-user reducer sets selectedWorkspace atomically with userData — so a
  // null client here means the user genuinely has no workspace (not a transient
  // load), the same case ModuleProtectedRoute denies. Without this, a null
  // client leaves useWorkflowVisibility stuck in 'loading' and the guard would
  // render the fallback indefinitely.
  if (!selectedClient) return <ModuleAccessDenied />;

  // Wait for the registry to resolve before deciding. Denying while the fetch is
  // in flight would flash "Access denied" on every load for a workspace that
  // actually has the page enabled. useWorkflowVisibility fails closed after its
  // retries, so a terminal error resolves to `status === 'error'` and lands on
  // the denied branch below — not here.
  if (status === "loading") return <PageLoadingFallback />;

  if (isWorkflowVisible(status, visibleWorkflowIds, workflowId)) return children;

  return <ModuleAccessDenied />;
};

export default WorkflowVisibilityRoute;
